import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCartContext } from '../../context/CartContext';

const Modal = ({
  onHideModal,
  onAutreCarteModal,
  onValiderAchat,
  onDeleteItem,
  itemToDelete,
  store,
  previewUrl,
  avsOptional,
  avs,
  onKeepAddress,
  onEditAddress,
  onUseAddress,
  modalType
}) => {
  const { t } = useTranslation();
  const { cart } = useCartContext();
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Enregistrer l'élément qui avait le focus avant l'ouverture
  useEffect(() => {
    previousFocusRef.current = document.activeElement;

    return () => {
      // Restaurer le focus quand la modal se ferme
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onHideModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    if (modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length > 0) {
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        firstElement.focus();

        const trapFocus = (e) => {
          if (e.key === 'Tab' && !e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }

          if (e.key === 'Tab' && e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        };

        document.addEventListener('keydown', trapFocus);

        return () => {
          document.removeEventListener('keydown', handleKeyDown);
          document.removeEventListener('keydown', trapFocus);
        };
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onHideModal]);

  // Empêcher le scroll du body quand la modal est ouverte
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Déterminer le titre approprié selon le type de modal
  const getModalTitle = () => {
    switch (modalType) {
      case 'cart':
        return t('Panier');
      case 'delete':
        return t('Confirmation de suppression');
      case 'preview':
        return t('Aperçu de la carte');
      case 'avs':
        return t('Vérification d\'adresse');
      case 'bankTransfer':
        return t('Information sur le virement bancaire');
      default:
        return t('Information');
    }
  };

  // Fonction helper pour rendre les liens accessibles
  const AccessibleLink = ({ onClick, className, children, ariaLabel }) => (
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();  
        onClick(e);
      }}
      className={className}
      aria-label={ariaLabel || undefined}
      role="button"
      tabIndex="0"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e);
        }
      }}
    >
      {children}
    </a>
  );

  return (
    <div
      id="modal"
      style={{ display: 'block' }}
      className={!store.exception ? 'modal-overlay' : 'modal-overlay hidden'}
      aria-hidden={store.exception ? "true" : "false"}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        id="modalBox"
        className="centerPopup"
        ref={modalRef}
        tabIndex="-1"
      >
        <h2 id="modal-title" className="sr-only">{getModalTitle()}</h2>
        <AccessibleLink
          onClick={onHideModal}
          className="closeInfoContent info"
          ariaLabel={t('Fermer')}
        >

        </AccessibleLink>

        {/* Modal Cart */}
        <div
          id="cartModal"
          className="modalcontent"
          style={{ display: modalType === 'cart' ? 'block' : 'none' }}
          role={modalType === 'cart' ? 'region' : undefined}
          aria-labelledby={modalType === 'cart' ? "cart-modal-title" : undefined}
        >
          {cart.items && cart.items.length > 0 ? (
            <div className="cartElems">
              <div className="rfEmptyCart">
                <div className="emtyCartHead">
                  <i className="icon-panier" aria-hidden="true"></i>
                </div>
                <p id="cart-modal-title">{t('La carte a été ajoutée à votre panier')}</p>
              </div>
              <div className="modalBtnsRow">
                <div className="col-6 alignCenter">
                  <AccessibleLink
                    onClick={onAutreCarteModal}
                    className="rfBtn buyOther"
                  >
                    {t('Acheter une autre carte')}
                  </AccessibleLink>
                </div>
                <div className="col-6 alignCenter">
                  <AccessibleLink
                    onClick={onValiderAchat}
                    className="rfBtn sendOrder"
                  >
                    {t('Valider mon achat')}
                  </AccessibleLink>
                </div>
              </div>
            </div>
          ) : (
              <div className="rfEmptyCart">
                <div className="emtyCartHead">
                  <i className="icon-panier" aria-hidden="true"></i>
                </div>
                <p id="cart-modal-title">{t('Votre panier est vide')}</p>
              </div>
            )}
        </div>

        {/* Modal Delete Confirmation */}
        <div
          id="deleteCartItem"
          className="modalcontent deleteModal"
          style={{ display: modalType === 'delete' ? 'block' : 'none' }}
          role={modalType === 'delete' ? 'alertdialog' : undefined}
          aria-labelledby={modalType === 'delete' ? "delete-modal-title" : undefined}
          aria-describedby={modalType === 'delete' ? "delete-modal-desc" : undefined}
        >
          <div className="deleteRequest">
            <i className="icon-trash" aria-hidden="true"></i>
            <p id="delete-modal-title">{t('Confirmation de suppression')}</p>
            <p id="delete-modal-desc">{t('Êtes-vous sûr de vouloir supprimer la carte ?')}</p>
          </div>
          <div className="modalBtnsRow">
            <div className="col-6 alignCenter">
              <AccessibleLink
                onClick={() => onDeleteItem(itemToDelete)}
                className="rfBtn sendOrder"
              >
                {t('Supprimer')}
              </AccessibleLink>
            </div>
            <div className="col-6 alignCenter">
              <AccessibleLink
                onClick={onHideModal}
                className="rfBtn buyOther"
              >
                {t('Non')}
              </AccessibleLink>
            </div>
          </div>
        </div>

        {/* Modal Preview Card */}
        <div
          id="previewCardModal"
          className="modalcontent"
          style={{ display: modalType === 'preview' ? 'block' : 'none' }}
          role={modalType === 'preview' ? 'region' : undefined}
          aria-labelledby={modalType === 'preview' ? "preview-modal-title" : undefined}
        >
          <h3 id="preview-modal-title" className="sr-only">{t('Aperçu de la carte')}</h3>
          <div className="scrollable">
            <iframe
              className="templateArea"
              src={previewUrl}
              title={t('Aperçu de la carte')}
              sandbox="allow-same-origin allow-scripts"
              referrerPolicy="origin"
            ></iframe>
          </div>
        </div>

        {/* par virement : information pour stripe */}
        <div
          id="bankTransferModal"
          className="modalcontent"
          style={{ display: modalType === 'bankTransfer' ? 'block' : 'none' }}
          role={modalType === 'bankTransfer' ? 'region' : undefined}
          aria-labelledby={modalType === 'bankTransfer' ? "bank-transfer-modal-title" : undefined}
        >
          <div className="bankTransferInfo">
            <i className="icon-info" aria-hidden="true" style={{ fontSize: '2em', marginBottom: '15px', display: 'block' }}></i>
            <h3 id="bank-transfer-modal-title" className="typoMedium" style={{ marginBottom: '15px' }}>
              {t('Information sur le virement bancaire')}
            </h3>
            <p style={{ marginBottom: '10px' }}>
              {t('Le paiement par virement bancaire fonctionne différemment des autres moyens de paiement. Voici ce qui va se passer :')}
            </p>
            <p style={{ marginBottom: '10px' }}>
              {t('Une fois votre commande validée, Stripe va vous attribuer un IBAN virtuel unique et permanent lié à votre compte. Vous devrez ensuite effectuer votre virement depuis votre espace bancaire vers cet IBAN.')}
            </p>
            <p style={{ marginBottom: '20px' }}>
              <strong>⚠️ {t('La confirmation du paiement peut prendre entre 0 et 3 jours ouvrés. Vos e-cartes cadeaux vous seront envoyées par mail uniquement après réception et confirmation du virement.')}</strong>
            </p>
          </div>
          <div className="modalBtnsRow">
            <div className="alignCenter" style={{ width: '100%' }}>
              <AccessibleLink
                onClick={onHideModal}
                className="rfBtn sendOrder"
              >
                {t("J'ai compris")}
              </AccessibleLink>
            </div>
          </div>
        </div>

        {/* Modal AVS */}
        <div
          id="resultAvs"
          className="modalcontent"
          style={{ display: modalType === 'avs' ? 'block' : 'none' }}
          role={modalType === 'avs' ? 'region' : undefined}
          aria-labelledby={modalType === 'avs' ? "avs-modal-title" : undefined}
        >
          <h3 id="avs-modal-title">{t('Vérification d\'adresse')}</h3>
          <div className="scrollable">
            <p>{t('Cette adresse nécessite une confirmation')}</p>
            <h4>{t('Ce que vous avez saisi')}</h4>
            <div className="address" aria-label={t('Adresse saisie')}>
              {avs?.verif?.address1 && <span>{avs.verif.address1}</span>}
              {avs?.verif?.address2 && <span>{avs.verif.address2}</span>}
              {avs?.verif?.address3 && <span>{avs.verif.address3}</span>}
              {avs?.verif?.address4 && <span>{avs.verif.address4}</span>}
              {avs?.verif?.zipCode && <span>{avs.verif.zipCode}</span>}
              {avs?.verif?.city && <span>{avs.verif.city}</span>}
              {avs?.verif?.state && <span>{avs.verif.state}</span>}
              {avs?.verif?.country && <span>{avs.verif.country}</span>}
            </div>

            <div className="modalBtnsRow">
              {avsOptional && (
                <div className="col-6 alignCenter">
                  <AccessibleLink
                    onClick={onKeepAddress}
                    className="rfBtn buyOther"
                  >
                    {t('Conserver cette adresse')}
                  </AccessibleLink>
                </div>
              )}
              <div className="col-6 alignCenter">
                <AccessibleLink
                  onClick={onEditAddress}
                  className="rfBtn buyOther"
                >
                  {t('Modifier mon adresse')}
                </AccessibleLink>
              </div>
            </div>

            {avs?.result && (
              <div className="addressRecommended">
                <h4>{t('Adresse recommandée')}</h4>
                <div aria-label={t('Adresse recommandée')}>
                  {avs.result.address1 && <span>{avs.result.address1}</span>}
                  {avs.result.address2 && <span>{avs.result.address2}</span>}
                  {avs.result.address3 && <span>{avs.result.address3}</span>}
                  {avs.result.address4 && <span>{avs.result.address4}</span>}
                  {avs.result.zipCode && <span>{avs.result.zipCode}</span>}
                  {avs.result.city && <span>{avs.result.city}</span>}
                  {avs.result.state && <span>{avs.result.state}</span>}
                  {avs.result.country && <span>{avs.result.country}</span>}
                </div>

                <div className="modalBtnsRow">
                  <div className="col-3 alignCenter">
                    <AccessibleLink
                      onClick={onUseAddress}
                      className="rfBtn useAddress"
                    >
                      {t('Utiliser cette adresse')}
                    </AccessibleLink>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;